import React, { useRef, useState, useCallback, useId } from 'react';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const roundToStep = (v, step, min) => {
    if (!step) return v;
    const steps = Math.round((v - min) / step);
    return clamp(min + steps * step, min, Infinity);
};

// Sweep geometry: a 270° arc with a 90° gap centered at the bottom (6 o'clock).
// Standard recipe for an SVG-circle-as-gauge: rotate = 90 + (360 - sweepDeg) / 2.
const SWEEP_DEG = 270;
const ARC_ROTATE = 90 + (360 - SWEEP_DEG) / 2; // 135

/**
 * A chrome-styled rotary knob. Drag vertically to change the value (relative
 * delta, not absolute angle-following — the standard pro-audio-UI pattern,
 * since angle-following jumps the value the instant the cursor isn't exactly
 * on the current pointer angle). Also supports mouse wheel, Shift for fine
 * control, double-click to reset, and full keyboard control.
 */
const RotaryKnob = ({
    value,
    onChange,
    min = 0,
    max = 1,
    step = 0.01,
    defaultValue = min,
    label,
    displayValue,
    size = 72,
    accentColor = '#d946ef',
    disabled = false,
}) => {
    const dragState = useRef(null);
    const [dragging, setDragging] = useState(false);
    const uid = useId();

    const pct = (value - min) / (max - min);
    const pointerAngle = -135 + pct * SWEEP_DEG;

    const r = 42;
    const circumference = 2 * Math.PI * r;
    const sweepLength = circumference * (SWEEP_DEG / 360);
    const valueLength = sweepLength * pct;

    const handlePointerDown = useCallback((e) => {
        if (disabled) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        dragState.current = { startY: e.clientY, startValue: value };
        setDragging(true);
    }, [disabled, value]);

    const handlePointerMove = useCallback((e) => {
        if (!dragState.current) return;
        const { startY, startValue } = dragState.current;
        const divisor = e.shiftKey ? 600 : 150;
        const delta = ((startY - e.clientY) / divisor) * (max - min);
        onChange(roundToStep(clamp(startValue + delta, min, max), step, min));
    }, [max, min, onChange, step]);

    const handlePointerUp = useCallback((e) => {
        dragState.current = null;
        setDragging(false);
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
    }, []);

    const handleWheel = useCallback((e) => {
        if (disabled) return;
        e.preventDefault();
        const dir = e.deltaY < 0 ? 1 : -1;
        onChange(clamp(value + dir * step, min, max));
    }, [disabled, max, min, onChange, step, value]);

    const handleDoubleClick = useCallback(() => {
        if (disabled) return;
        onChange(defaultValue);
    }, [defaultValue, disabled, onChange]);

    const handleKeyDown = useCallback((e) => {
        if (disabled) return;
        if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
            e.preventDefault();
            onChange(clamp(value + step, min, max));
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
            e.preventDefault();
            onChange(clamp(value - step, min, max));
        } else if (e.key === 'Home') {
            e.preventDefault();
            onChange(min);
        } else if (e.key === 'End') {
            e.preventDefault();
            onChange(max);
        }
    }, [disabled, max, min, onChange, step, value]);

    return (
        <div className="flex flex-col items-center gap-1.5 select-none" style={{ touchAction: 'none' }}>
            <svg
                width={size}
                height={size}
                viewBox="0 0 100 100"
                role="slider"
                aria-label={label}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={value}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onWheel={handleWheel}
                onDoubleClick={handleDoubleClick}
                onKeyDown={handleKeyDown}
                className={`outline-none ${disabled ? 'opacity-40 cursor-not-allowed' : dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{ filter: dragging ? `drop-shadow(0 0 6px ${accentColor}aa)` : undefined }}
            >
                <defs>
                    <linearGradient id={`knob-bezel-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f3f4f6" />
                        <stop offset="40%" stopColor="#9ca3af" />
                        <stop offset="70%" stopColor="#4b5563" />
                        <stop offset="100%" stopColor="#1f2937" />
                    </linearGradient>
                    <radialGradient id={`knob-face-${uid}`} cx="35%" cy="30%" r="75%">
                        <stop offset="0%" stopColor="#4b5563" />
                        <stop offset="60%" stopColor="#26282c" />
                        <stop offset="100%" stopColor="#101113" />
                    </radialGradient>
                </defs>

                {/* Outer chrome bezel */}
                <circle cx="50" cy="50" r="46" fill={`url(#knob-bezel-${uid})`} />
                {/* Domed inner face */}
                <circle cx="50" cy="50" r="38" fill={`url(#knob-face-${uid})`} />

                {/* Track arc (dim, full sweep) */}
                <circle
                    cx="50" cy="50" r={r}
                    fill="none"
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${sweepLength} ${circumference - sweepLength}`}
                    transform={`rotate(${ARC_ROTATE} 50 50)`}
                />
                {/* Value arc */}
                <circle
                    cx="50" cy="50" r={r}
                    fill="none"
                    stroke={accentColor}
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={`${valueLength} ${circumference - valueLength}`}
                    transform={`rotate(${ARC_ROTATE} 50 50)`}
                    style={{ transition: dragging ? 'none' : 'stroke-dasharray 150ms ease' }}
                />

                {/* Rotating pointer */}
                <g transform={`rotate(${pointerAngle} 50 50)`} style={{ transition: dragging ? 'none' : 'transform 150ms ease' }}>
                    <line x1="50" y1="50" x2="50" y2="22" stroke={accentColor} strokeWidth="3" strokeLinecap="round" />
                    <circle cx="50" cy="22" r="2.5" fill={accentColor} />
                </g>
            </svg>

            {label && <span className="text-xs text-gray-400 font-medium">{label}</span>}
            {displayValue && <span className="text-xs text-gray-500 tabular-nums">{displayValue(value)}</span>}
        </div>
    );
};

export default RotaryKnob;
